import api from "./api";
import Logger from "../utils/Logger";

interface AttributeResponse {
  responseObject: Attribute[];
}

interface Attribute {
  ACTIVE_IND: boolean;
  ATTRIBUTE_ID: number;
  ATTRIBUTE_NM: string;
  CALENDAR_YEAR: number;
  CATEGORY_DESC: string;
  CATEGORY_ID: number;
  CATEGORY_NM: string;
  FROM_DT: string;
}

export interface AttributeListItem {
  id: string;
  name: string;
  calendarYear: number;
  fromDate: string;
  active: boolean;
  categoryId: number;
  categoryName: string;
  categoryDescription: string;
}

// Constants for categories
export const ATTRIBUTE_CATEGORIES = {
  OVERALL: "overall",
  ENVIRONMENTAL: "environmental",
  HUMANRIGHTS: "humanrights",
} as const;

export type AttributeCategory =
  (typeof ATTRIBUTE_CATEGORIES)[keyof typeof ATTRIBUTE_CATEGORIES];

/**
 * Maps an Attribute to an AttributeListItem.
 * @param attribute - The attribute to map.
 * @returns The mapped AttributeListItem.
 */
const mapAttributeToAttributeListItem = (
  attribute: Attribute,
): AttributeListItem => ({
  id: attribute.ATTRIBUTE_ID.toString(),
  name: attribute.ATTRIBUTE_NM,
  calendarYear: attribute.CALENDAR_YEAR,
  fromDate: attribute.FROM_DT,
  active: attribute.ACTIVE_IND,
  categoryId: attribute.CATEGORY_ID,
  categoryName: attribute.CATEGORY_NM,
  categoryDescription: attribute.CATEGORY_DESC,
});

/**
 * Fetches attributes from the API based on the provided category and active flag.
 * Defaults: category = "overall", active = true.
 * @param category - The category of attributes to fetch.
 * @param active - Whether to fetch only active attributes.
 * @returns A list of AttributeListItem.
 */
const attributesFromRemote = async (
  category: AttributeCategory = ATTRIBUTE_CATEGORIES.OVERALL,
  active: boolean = true,
): Promise<AttributeListItem[]> => {
  try {
    Logger.info(
      `Fetching attributes from API - category: ${category}, active: ${active}`,
      ["AttributeApi"],
    );

    const attributeResult = await api.get<AttributeResponse>(
      `/attribute?category=${category}&active=${active}`,
    );

    if (!attributeResult || !attributeResult.data) {
      Logger.error("API response is null or undefined", null, ["AttributeApi"]);
      return [];
    }

    const data = attributeResult.data;

    if (!data.responseObject || !Array.isArray(data.responseObject)) {
      Logger.error(
        "API responseObject is invalid or not an array",
        data.responseObject,
        ["AttributeApi"],
      );
      return [];
    }

    const attributeListItems: AttributeListItem[] = data.responseObject.map(
      mapAttributeToAttributeListItem,
    );

    Logger.info(
      `Successfully fetched ${attributeListItems.length} attributes`,
      ["AttributeApi"],
    );

    try {
      sessionStorage.setItem(
        `attributeListItemsStored_${category}_${active}`,
        JSON.stringify(attributeListItems),
      );
      Logger.debug("Cached attributes in session storage", ["AttributeApi"]);
    } catch (storageError) {
      Logger.error("Error saving data to sessionStorage", storageError, [
        "AttributeApi",
      ]);
    }

    return attributeListItems;
  } catch (err) {
    Logger.error("Error fetching attribute data", err, ["AttributeApi"]);
    return [];
  }
};

/**
 * Retrieves the attribute list from session storage or fetches it from the API.
 * @param category - The category of attributes to fetch.
 * @param active - Whether to fetch only active attributes.
 * @returns A list of AttributeListItem.
 */
const getAttributeList = async (
  category: AttributeCategory = ATTRIBUTE_CATEGORIES.OVERALL,
  active: boolean = true,
): Promise<AttributeListItem[]> => {
  const storageKey = `attributeListItemsStored_${category}_${active}`;
  const storedAttributeListItems = sessionStorage.getItem(storageKey);

  if (storedAttributeListItems) {
    Logger.debug(`Loading attributes from cache - ${storageKey}`, [
      "AttributeApi",
    ]);

    try {
      const parsedData = JSON.parse(storedAttributeListItems);

      if (!Array.isArray(parsedData)) {
        throw new Error("Parsed data is not an array");
      }

      Logger.info(`Loaded ${parsedData.length} attributes from cache`, [
        "AttributeApi",
      ]);
      return parsedData;
    } catch (err) {
      Logger.error("Error parsing stored attribute data", err, [
        "AttributeApi",
      ]);
      sessionStorage.removeItem(storageKey);
    }
  }

  Logger.debug("No cached attributes found, fetching from remote", [
    "AttributeApi",
  ]);
  return await attributesFromRemote(category, active);
};

export const AttributeApi = {
  getAttributeList,
};
